import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Box, CircularProgress, Tooltip} from "@mui/material";
import {Add, AssessmentOutlined, GroupsOutlined, LockReset} from "@mui/icons-material";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Row} from "reactstrap";
import Button from "../../../../../components/base/button/button";
import IconButton from "../../../../../components/base/button/icon_button";
import SearchInput from "../../../../../components/base/input/search_input";
import Api from "../../../../../../core/services/api/api";
import AddOperatorModal from "./modal/add_operator";
import EditOperatorGroupsModal from "./modal/edit_groups";
import OperatorResetPasswordModal from "../../../../../components/dialogs/operator-reset-password";
import {formatPhoneNumber} from "../../../../../../core/services/utils/utils";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import {ReactComponent as ImagePlaceholder} from "../../../../../../assets/images/operator-profile-placeholder/operator-profile-placeholder-sidebar.svg";
import LazyImage from "../../../../../components/app-specific/lazy-image";
import {OperatorsListDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../containers/data-grid";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../core/services/date-utils";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import CacheService from "../../../../../../core/services/cache/cache_service";
import CompetitorPriceReportsPopover from "../../../../../components/app-specific/draggable-popover/competitor-price-reports-popover";
import {useOperatorInformation} from "../../../../../hooks/use-operator-information";

const initOperatorPassword = {operator: null, password: null, open: false};

const dataGridSaveConfig = {
    name: 'CurrentOperatorsListViewDataGrid',
    version: 4,
}

const exportingChunkSize = 100;

const CurrentOperatorsListView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.operators-current'});
    const operatorAccessLevels = useAccessLevels();
    const operatorInformation = useOperatorInformation();
    const currentOperatorId = operatorInformation?.id;
    const canViewAllCompetitorPriceReports = operatorAccessLevels.packages.lookup.viewAllCompetitorPriceReports;
    const canViewMyCompetitorPriceReports = operatorAccessLevels.packages.lookup.viewMyCompetitorPriceReports;
    const competitorPriceReportsMineOnly = !canViewAllCompetitorPriceReports && canViewMyCompetitorPriceReports;
    const canViewAnyCompetitorPriceReports = canViewAllCompetitorPriceReports || canViewMyCompetitorPriceReports;
    const canManageOperators = operatorAccessLevels.packages.base.manageOperators;
    const showActions = canViewAnyCompetitorPriceReports || canManageOperators;
    const actionCount = (canViewAnyCompetitorPriceReports ? 1 : 0) + (canManageOperators ? 2 : 0);
    const [loadingGroups, setLoadingGroups] = useState(true);
    const [operators, setOperators] = useState([]);
    const [operatorGroups, setOperatorGroups] = useState([]);
    const [openAddOperator, setOpenAddOperator] = useState(false);
    const [openGroups, setOpenGroups] = useState(false);
    const [operator, setOperator] = useState(false);
    const [operatorPassword, setOperatorPassword] = useState(initOperatorPassword)
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('current-operators-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.operatorsList);
        configureBehavioralTool((tags) => tags.operatorsList);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Gets all the operator groups available to be assigned to different users.
     */
    const getAllGroups = useCallback(() => {
        setLoadingGroups(true);
        Api.getAllOperatorGroups().then((response) => {
            if (!isMounted()) return;
            setOperatorGroups(response?.data ?? []);
            setLoadingGroups(false);
        })
    }, [isMounted])

    /**
     * Gets all the available user groups as soon as the component mounts
     */
    useEffect(() => {
        getAllGroups()
    }, [getAllGroups])

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(showActions ? [{
                name: OperatorsListDataGridCellKeys.actions,
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.center,
                sortable: false,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.left,
                pinnedToggleable: false,
                visibilityToggleable: false,
                resizable: false,
                width: (actionCount - 1) * 40,
            }] : []),
            {
                title: "First Name",
                name: OperatorsListDataGridCellKeys.firstName,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                pinnedType: DataGridColumnPinnedTypes.left,
                sortable: false,
                width: 120,
            },
            {
                title: "Last Name",
                name: OperatorsListDataGridCellKeys.lastName,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Email",
                name: OperatorsListDataGridCellKeys.email,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 200,
            },
            {
                title: "Phone",
                name: OperatorsListDataGridCellKeys.phone,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Last Login Date",
                name: OperatorsListDataGridCellKeys.lastLoginDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                sortable: false,
                width: 150,
            },
        ], [actionCount, showActions])

    const [dataGridColumns, , dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Prepares the api call request body based on the filters, orderBy, and pagination
     * - Based on the characteristic of the keyword, sets the proper property of the filter
     * @type {(function(UseSearchFunctionArg): any)}
     */
    const prepareApiRequest = useCallback((args) => {
        let forApi;
        if (!!args.filters?.keyword) {
            if (args.filters.keyword.toString().indexOf('@') !== -1) {
                forApi = {
                    filters: {
                        email: args.filters.keyword
                    }
                }
            } else if (/\d/.test(args.filters.keyword)) {
                forApi = {
                    filters: {
                        phone: args.filters.keyword
                    }
                }
            } else {
                forApi = {
                    filters: {
                        name: args.filters.keyword
                    }
                }
            }
        }
        forApi = {
            ...forApi,
            paginationInfo: args.paginationInfo,
        }
        return forApi;
    }, [])

    /**
     * Fetches the list of operators by calling the api regarding this action
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = prepareApiRequest(args);
        const response = await Api.searchOperatorsLocal(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setOperators(response?.data?.resultList?.map(e => ({...e, resettingPassword: false})) ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.paginationInfo?.length ?? 0
            }))
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, prepareApiRequest])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
    }), [initialPaginationInfo]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Resets operator's password in the server and if the result of the api is successful, opens a dialog with the
     * new password.
     * @param operator
     */
    const resetOperatorPassword = useCallback((operator) => {
        setOperators(prevState => prevState.map((e => e.id === operator.id ? {...e, resettingPassword: true} : e)))
        const forApi = {
            id: operator.id
        };
        Api.resetOperatorPassword(forApi).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setOperatorPassword({operator, password: response.data, open: true})
            }
            setOperators(prevState => prevState.map((e => e.id === operator.id ? {...e, resettingPassword: false} : e)))
        })
    }, [isMounted])

    /**
     * Handles changes of the keyword in search input and triggers navigating to this page with entered keyword in query params
     * @param keywords
     */
    const searchByKeyword = (keywords) => {
        searchData.setFilters(prevState => ({
            ...prevState,
            keyword: keywords,
        }));
    }

    /**
     * Adds the newly created operator to the beginning of the list and slices the list to show the maximum of
     * [initialPaginationInfo.pageSize] number of items
     *
     * @param newOperator {any} the newly created operator
     */
    const onAddSuccess = (newOperator) => {
        if (newOperator) {
            setOperators(prevState => [newOperator, ...prevState].slice(0, initialPaginationInfo.pageSize))
        } else {
            void searchData.performSearch();
        }
    }

    /**
     * Opens the modal for adding / removing operator groups
     * @param operator {any}
     */
    const openOperatorGroups = useCallback((operator) => {
        setOperator(operator);
        setOpenGroups(true);
    }, [])

    /**
     * Opens a confirmation dialog asking to reset operator's password.
     * @param operator
     */
    const onResetPassword = useCallback((operator) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to reset {operator.firstName ?? ''} {operator.lastName ?? ''}'s password?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && resetOperatorPassword(operator),
        );
    }, [resetOperatorPassword])

    /**
     * Formats the exported data's entries and returns the info to be exported.
     * @param {any[]} list
     * @return {DataGridExportInfo}
     */
    const formatExportingData = useCallback((list) => {
        const exportTitle = `Operators of the system`;
        return {
            title: exportTitle,
            body: list?.map((item, index) => ({
                '': index + 1,
                'First Name': item.firstName ?? '',
                'Last Name': item.lastName ?? '',
                'Phone': formatPhoneNumber(item?.phone) ?? '',
                "Email": item.email ?? '',
                "Last Login Date and Time": !!item.lastLoginDate ? DateUtils.serverToLocal(item?.lastLoginDate).format(DateUtils.dateTimeFormat) : '--',
            })) ?? [],
        }
    }, [])

    /**
     * Exports the entries of this view by searching over all the entries in the system.
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const canExportOperatorsList = operatorAccessLevels.packages.base.exportOperatorsList;
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.resultList?.length) {
                break;
            }
            entries.push(...res.data.resultList);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData])

    /**
     * Memo version of table row records used in data grid
     */
    const rows = useMemo(() => {
        return operators?.map(operator => ({
            key: operator?.id,
            data: operator,
            cells: {
                [OperatorsListDataGridCellKeys.firstName]: (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <div className={'operator-image-profile-list'}>
                            {
                                <LazyImage
                                    alt={'profile'}
                                    noImage={<ImagePlaceholder/>}
                                    src={CacheService.getOperatorProfileImage(operator?.id)}
                                    height={40}
                                />
                            }
                        </div>
                        {operator?.firstName ?? ""}
                    </Box>
                ),
                [OperatorsListDataGridCellKeys.lastName]: operator?.lastName,
                [OperatorsListDataGridCellKeys.email]: operator?.email,
                [OperatorsListDataGridCellKeys.phone]: formatPhoneNumber(operator?.phone),
                [OperatorsListDataGridCellKeys.lastLoginDate]: operator.lastLoginDate,
                [OperatorsListDataGridCellKeys.actions]: showActions
                    ? (
                        <Box sx={{display: 'flex', gap: 1, justifyContent: 'center'}}>
                            {(canViewAllCompetitorPriceReports
                                || (competitorPriceReportsMineOnly && String(operator.id) === String(currentOperatorId))) && (
                                <Tooltip title={t('actions.viewReports')}>
                                    <Box component={'span'} sx={{display: 'inline-flex'}}>
                                        <CompetitorPriceReportsPopover
                                            operatorId={operator.id}
                                            operatorName={[operator.firstName, operator.lastName].filter(Boolean).join(' ')}
                                            mineOnly={competitorPriceReportsMineOnly}
                                            buttonProps={{'aria-label': t('actions.viewReports')}}
                                        >
                                            <AssessmentOutlined className={'icon medium'}/>
                                        </CompetitorPriceReportsPopover>
                                    </Box>
                                </Tooltip>
                            )}
                            {canManageOperators && (
                                <>
                                    <Tooltip title={t('actions.manageGroups')}>
                                        <span>
                                            <IconButton
                                                color={'paper.primary'}
                                                size={'medium'}
                                                aria-label={t('actions.manageGroups')}
                                                disabled={loadingGroups}
                                                onPressed={() => openOperatorGroups(operator)}
                                            >
                                                <GroupsOutlined className={'icon medium'}/>
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={t('actions.resetPassword')}>
                                        <span>
                                            <IconButton
                                                color={'paper.error'}
                                                size={'medium'}
                                                aria-label={t('actions.resetPassword')}
                                                disabled={operator.resettingPassword}
                                                onPressed={() => onResetPassword(operator)}
                                            >
                                                {operator.resettingPassword
                                                    ? <CircularProgress size={20}/>
                                                    : <LockReset className={'icon medium'}/>}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </>
                            )}
                        </Box>
                    )
                    : null,
            }
        })) ?? []
    }, [
        canManageOperators,
        canViewAllCompetitorPriceReports,
        competitorPriceReportsMineOnly,
        currentOperatorId,
        loadingGroups,
        onResetPassword,
        openOperatorGroups,
        operators,
        showActions,
        t,
    ])


    return (
        <>
            <Row className='no-gutters w-100 justify-content-between align-items-center my-4'>
                <h2 className={'text-xl font-weight-semi-bold m-0 ml-3'}>
                    Operators
                </h2>
                {
                    canManageOperators &&
                    <Button
                        className='mr-2 d-flex align-items-center justify-content-center'
                        variant={'outlined'}
                        color={'primary'}
                        onClick={() => setOpenAddOperator(true)}
                    >
                        <Add className={`icon small`} style={{margin: '2px 0'}}/>
                        <p className='ml-2'>Add new</p>
                    </Button>
                }
            </Row>
            <SearchInput
                loading={searchData.loading}
                autoSearch
                search={searchByKeyword}
                initialValue={searchData.filters?.keyword ?? undefined}

                label={'Search'}
            />
            <div className='w-100 my-5'/>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={rows}
                hideFooterActions={true}
                state={{
                    pagination: searchData.paginationInfo,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                getExportInfo={canExportOperatorsList ? exportToCsv : undefined}
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
            />
            <AddOperatorModal
                open={openAddOperator}
                setOpen={setOpenAddOperator}
                onSuccess={onAddSuccess}
            />
            <EditOperatorGroupsModal
                open={openGroups}
                setOpen={setOpenGroups}
                allGroups={operatorGroups}
                operator={operator}
                onSuccess={() => {
                }}
            />
            <OperatorResetPasswordModal
                open={operatorPassword.open}
                setOpen={() => setOperatorPassword(initOperatorPassword)}
                operator={operatorPassword.operator}
                password={operatorPassword.password}
            />
        </>
    );
}

export default CurrentOperatorsListView;
